import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';

const Inspector = ({ machine, selectedNode }) => {
  if (!selectedNode || !machine?.states?.[selectedNode]) {
    return (
      <div className="p-4 text-center text-gray-500" data-testid="inspector-empty">
        <div className="text-3xl mb-3">🔍</div>
        <p className="text-xs">Select a state to inspect</p>
      </div>
    );
  }

  const state = machine.states[selectedNode];

  return (
    <ScrollArea className="h-full" data-testid="inspector">
      <div className="p-3 space-y-4">
        <div>
          <h3 className="text-sm font-bold mb-2 font-mono">{selectedNode}</h3>
          <Badge variant="outline" className="text-[10px] h-5">{state.type}</Badge>
        </div>

        {state.entry && state.entry.length > 0 && (
          <div>
            <h4 className="text-xs font-mono text-gray-500 mb-2">ENTRY</h4>
            <div className="space-y-1">
              {state.entry.map((effect, idx) => (
                <div
                  key={idx}
                  className="px-2 py-1.5 bg-green-500/10 border border-green-500/30 rounded text-xs font-mono"
                >
                  {effect}
                </div>
              ))}
            </div>
          </div>
        )}

        {state.exit && state.exit.length > 0 && (
          <div>
            <h4 className="text-xs font-mono text-gray-500 mb-2">EXIT</h4>
            <div className="space-y-1">
              {state.exit.map((effect, idx) => (
                <div
                  key={idx}
                  className="px-2 py-1.5 bg-red-500/10 border border-red-500/30 rounded text-xs font-mono"
                >
                  {effect}
                </div>
              ))}
            </div>
          </div>
        )}

        {state.transitions && state.transitions.length > 0 && (
          <div>
            <h4 className="text-xs font-mono text-gray-500 mb-2">TRANSITIONS</h4>
            <div className="space-y-2">
              {state.transitions.map((trans, idx) => (
                <div
                  key={idx}
                  className="p-2 bg-white/5 border border-white/10 rounded space-y-1.5"
                >
                  <div className="flex items-center space-x-2 text-xs">
                    <span className="text-blue-400 font-mono font-medium">
                      {trans.event}
                    </span>
                    <span className="text-gray-600">→</span>
                    <span className="font-mono font-medium">{trans.target}</span>
                  </div>
                  {trans.guards && trans.guards.length > 0 && (
                    <div className="text-[10px] text-gray-500 font-mono">
                      Guards: {trans.guards.map((g) => g.name).join(', ')}
                    </div>
                  )}
                  {trans.effects && trans.effects.length > 0 && (
                    <div className="text-[10px] text-gray-500 font-mono">
                      Effects: {trans.effects.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {state.invariants && state.invariants.length > 0 && (
          <div>
            <h4 className="text-xs font-mono text-gray-500 mb-2">INVARIANTS</h4>
            <div className="space-y-1.5">
              {state.invariants.map((inv, idx) => (
                <div
                  key={idx}
                  className="p-2 bg-purple-500/10 border border-purple-500/30 rounded text-[10px]"
                >
                  <div className="font-mono font-semibold">{inv.name}</div>
                  <div className="text-gray-500 mt-0.5">{inv.message}</div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </ScrollArea>
  );
};

export default Inspector;
